import { useEffect, useState } from "react";
import { ArrowUpRight, Instagram } from "lucide-react";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const IG_URL = "https://www.instagram.com/loan_salaah/";

export const InstagramStrip = () => {
  const [posts, setPosts] = useState(null); // null = feed not configured

  useEffect(() => {
    fetch(`${API}/instagram/feed`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`IG ${r.status}`))))
      .then((d) => setPosts((d.data || []).slice(0, 6)))
      .catch(() => setPosts([]));
  }, []);

  return (
    <section data-testid="instagram-strip" className="bg-cream pb-20 sm:pb-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="flex flex-col items-start justify-between gap-6 sm:flex-row sm:items-end">
          <div>
            <div className="flex items-center gap-4">
              <span className="brass-rule" aria-hidden="true" />
              <span className="font-mono text-[0.65rem] uppercase tracking-[0.3em] text-brass">On Instagram</span>
            </div>
            <h2 className="mt-5 font-serif text-3xl font-semibold tracking-tight text-concordia sm:text-4xl">
              Money wisdom, in your feed.
            </h2>
          </div>
          <a
            href={IG_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="instagram-follow-button"
            className="group inline-flex items-center gap-2 rounded-full bg-concordia px-7 py-3.5 text-sm font-semibold text-ivory transition-all duration-300 hover:-translate-y-0.5 hover:bg-concordia-deep"
          >
            <Instagram className="h-4 w-4 text-brass" />
            Follow @loan_salaah
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </a>
        </Reveal>

        {posts && posts.length > 0 ? (
          <Stagger className="mt-12 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
            {posts.map((p) => (
              <StaggerItem key={p.id}>
                <a
                  href={p.permalink}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`instagram-post-${p.id}`}
                  className="group relative block overflow-hidden rounded-2xl border border-sand"
                >
                  <img
                    src={p.media_type === "VIDEO" ? p.thumbnail_url : p.media_url}
                    alt="Latest from @loan_salaah on Instagram"
                    loading="lazy"
                    className="aspect-square w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <span className="absolute inset-0 flex items-center justify-center bg-concordia-ink/0 transition-colors duration-300 group-hover:bg-concordia-ink/40">
                    <Instagram className="h-6 w-6 text-ivory opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                  </span>
                </a>
              </StaggerItem>
            ))}
          </Stagger>
        ) : (
          <Reveal delay={0.1}>
            <div className="mt-12 flex flex-col items-center gap-5 rounded-3xl border border-sand bg-white p-10 text-center shadow-[0_20px_50px_rgba(20,40,75,0.06)]" data-testid="instagram-fallback">
              <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-concordia/[0.07] text-concordia">
                <Instagram className="h-7 w-7" />
              </span>
              <div>
                <p className="font-serif text-2xl font-semibold text-concordia">@loan_salaah</p>
                <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-slate">
                  Rate drops, smart-borrowing tips and behind-the-scenes from the Salaah desk — fresh on Instagram.
                  Your latest uploads appear here automatically once the feed is connected.
                </p>
              </div>
              <a
                href={IG_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="instagram-fallback-follow"
                className="inline-flex items-center gap-2 rounded-full border border-concordia/30 px-7 py-3 text-sm font-semibold text-concordia transition-colors hover:bg-concordia hover:text-ivory"
              >
                <Instagram className="h-4 w-4" />
                Open Instagram
              </a>
            </div>
          </Reveal>
        )}
      </div>
    </section>
  );
};
